import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API } from '../api/client';
import type { ApiError } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { cop, fecha, hoy, num, toDateValue } from '../lib/format';
import { downloadXlsx, FMT_COP, FMT_DATE, FMT_NUM, fileNameConFecha } from '../lib/xlsx';
import type { Categoria, Insumo, Movimiento, TipoMovimiento } from '../api/types';
import { PageHeader } from '../components/PageHeader';
import { FilterBar } from '../components/FilterBar';
import { ResponsiveTable, type Column } from '../components/ResponsiveTable';
import { MobileFormSheet } from '../components/MobileFormSheet';
import { useIsMobile } from '../lib/useMediaQuery';

type ListaMov = { items: Movimiento[]; total: number };

type FormMov = {
  id?: string;
  fecha: string;
  tipo: TipoMovimiento;
  categoriaId: string;
  subcategoriaId: string;
  insumoId: string;
  cantidad: string;
  unidad: string;
  descripcion: string;
  metodoPago: string;
  monto: string;
};

const defaults: FormMov = {
  fecha: hoy(),
  tipo: 'gasto',
  categoriaId: '',
  subcategoriaId: '',
  insumoId: '',
  cantidad: '',
  unidad: '',
  descripcion: '',
  metodoPago: '',
  monto: '',
};

function movimientoToForm(m: Movimiento): FormMov {
  return {
    id: m.id,
    fecha: m.fecha.slice(0, 10),
    tipo: m.tipo,
    categoriaId: m.categoriaId ?? '',
    subcategoriaId: m.subcategoriaId ?? '',
    insumoId: m.insumoId ?? '',
    cantidad: m.cantidad ?? '',
    unidad: m.unidad ?? m.insumo?.unidad ?? '',
    descripcion: m.descripcion ?? '',
    metodoPago: m.metodoPago ?? '',
    monto: m.monto,
  };
}

function tipoPillClass(t: TipoMovimiento): string {
  if (t === 'gasto') return 'bg-red-50 text-red-700';
  if (t === 'compra') return 'bg-amber-50 text-amber-700';
  if (t === 'ingreso') return 'bg-emerald-50 text-emerald-700';
  return 'bg-blue-50 text-blue-700';
}

function tipoLabel(t: TipoMovimiento): string {
  if (t === 'gasto') return 'Gasto';
  if (t === 'compra') return 'Compra';
  if (t === 'ingreso') return 'Ingreso';
  return 'Aporte';
}

function esEgreso(t: TipoMovimiento): boolean {
  return t === 'gasto';
}

function montoClass(t: TipoMovimiento): string {
  if (t === 'compra') return 'text-amber-700';
  return esEgreso(t) ? 'text-red-600' : 'text-emerald-700';
}

function montoPrefix(t: TipoMovimiento): string {
  if (t === 'compra') return '';
  return esEgreso(t) ? '-' : '+';
}

export function MovimientosPage() {
  const { tiene } = useAuth();
  const qc = useQueryClient();
  const isMobile = useIsMobile();
  const [filtroTipo, setFiltroTipo] = useState<'' | TipoMovimiento>('');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [incluirAutomaticos, setIncluirAutomaticos] = useState(false);
  const [form, setForm] = useState<FormMov>(defaults);
  const [error, setError] = useState<string | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  const lista = useQuery({
    queryKey: ['movimientos', filtroTipo, desde, hasta, incluirAutomaticos],
    queryFn: () => {
      const p = new URLSearchParams();
      if (filtroTipo) p.set('tipo', filtroTipo);
      if (desde) p.set('desde', desde);
      if (hasta) p.set('hasta', hasta);
      if (incluirAutomaticos) p.set('incluirAutomaticos', 'true');
      p.set('limit', '200');
      return API.get<ListaMov>(`/movimientos?${p.toString()}`);
    },
    enabled: tiene('movimientos:read'),
  });

  const saldoQ = useQuery({
    queryKey: ['saldo'],
    queryFn: () => API.get<{ saldo: number }>('/movimientos/saldo'),
    enabled: tiene('movimientos:read'),
  });

  const catsQ = useQuery({
    queryKey: ['categorias'],
    queryFn: () => API.get<Categoria[]>('/categorias'),
    enabled: tiene('categorias:read'),
  });

  const insumosQ = useQuery({
    queryKey: ['insumos'],
    queryFn: () => API.get<Insumo[]>('/insumos'),
    enabled: tiene('insumos:read'),
  });

  const categoriasDelTipo = useMemo(
    () => (catsQ.data ?? []).filter((c) => !c.padreId && (!c.tipoMovimientoPermitido || c.tipoMovimientoPermitido === form.tipo)),
    [catsQ.data, form.tipo],
  );
  const subcategorias = useMemo(
    () => (catsQ.data ?? []).filter((c) => c.padreId === form.categoriaId),
    [catsQ.data, form.categoriaId],
  );

  const insumoSeleccionado = useMemo(
    () => insumosQ.data?.find((i) => i.id === form.insumoId) ?? null,
    [insumosQ.data, form.insumoId],
  );
  const categoriaDelInsumo = useMemo(
    () => (catsQ.data ?? []).find((c) => c.id === insumoSeleccionado?.categoriaId) ?? null,
    [catsQ.data, insumoSeleccionado],
  );
  const ocultarCategoria = (form.tipo === 'gasto' || form.tipo === 'compra') && !!form.insumoId;

  const crear = useMutation({
    mutationFn: (body: Record<string, unknown>) => API.post<Movimiento>('/movimientos', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['movimientos'] });
      qc.invalidateQueries({ queryKey: ['saldo'] });
      qc.invalidateQueries({ queryKey: ['insumos'] });
      setForm(defaults);
      setError(null);
      setSheetOpen(false);
    },
    onError: (err) => setError((err as unknown as ApiError).message ?? 'Error al crear movimiento'),
  });

  const actualizar = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      API.put<Movimiento>(`/movimientos/${id}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['movimientos'] });
      qc.invalidateQueries({ queryKey: ['saldo'] });
      qc.invalidateQueries({ queryKey: ['insumos'] });
      setForm(defaults);
      setError(null);
      setSheetOpen(false);
    },
    onError: (err) => setError((err as unknown as ApiError).message ?? 'Error al actualizar movimiento'),
  });

  const eliminar = useMutation({
    mutationFn: (id: string) => API.del(`/movimientos/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['movimientos'] });
      qc.invalidateQueries({ queryKey: ['saldo'] });
      qc.invalidateQueries({ queryKey: ['insumos'] });
    },
  });

  function limpiarFormulario() {
    setForm(defaults);
    setError(null);
  }

  function editarMovimiento(m: Movimiento) {
    if (m.metodoPago === 'automatico') return;
    setForm(movimientoToForm(m));
    setError(null);
    if (isMobile) setSheetOpen(true);
  }

  function exportarXlsx() {
    const items = lista.data?.items ?? [];
    if (items.length === 0) return;
    downloadXlsx(fileNameConFecha('movimientos'), [
      {
        name: 'Movimientos',
        rows: items,
        columns: [
          { header: 'Fecha', value: (m) => toDateValue(m.fecha), width: 12, numFmt: FMT_DATE },
          { header: 'Tipo', value: (m) => m.tipo, width: 10 },
          { header: 'Categoría', value: (m) => m.categoria?.nombre ?? '', width: 20 },
          { header: 'Subcategoría', value: (m) => m.subcategoria?.nombre ?? '', width: 20 },
          { header: 'Insumo', value: (m) => m.insumo?.nombre ?? '', width: 18 },
          { header: 'Cantidad', value: (m) => (m.cantidad ? Number(m.cantidad) : null), width: 10, numFmt: FMT_NUM },
          { header: 'Unidad', value: (m) => m.unidad ?? m.insumo?.unidad ?? '', width: 8 },
          { header: 'Descripción', value: (m) => m.descripcion ?? '', width: 30 },
          { header: 'Método de pago', value: (m) => m.metodoPago ?? '', width: 14 },
          { header: 'Monto', value: (m) => Number(m.monto), width: 14, numFmt: FMT_COP },
          { header: 'Signo', value: (m) => (m.tipo === 'compra' ? 'inventario' : m.tipo === 'gasto' ? 'egreso' : 'ingreso'), width: 10 },
        ],
      },
    ]);
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const monto = Number(form.monto.replace(',', '.'));
    if (!Number.isFinite(monto) || monto <= 0) {
      setError('El monto debe ser positivo');
      return;
    }
    if (form.tipo === 'compra') {
      if (!form.insumoId) {
        setError('Selecciona el insumo que entra a stock.');
        return;
      }
      const cantidad = Number(form.cantidad.replace(',', '.'));
      if (!Number.isFinite(cantidad) || cantidad <= 0) {
        setError('La compra de materia prima debe tener una cantidad positiva.');
        return;
      }
      if (insumoSeleccionado && !insumoSeleccionado.conStock) {
        setError('Ese insumo no maneja stock. Activa stock en Insumos antes de comprarlo como materia prima.');
        return;
      }
    }
    const categoriaIdFinal = ocultarCategoria
      ? (insumoSeleccionado?.categoriaId ?? null)
      : (form.categoriaId || null);
    const subcategoriaIdFinal = ocultarCategoria ? null : (form.subcategoriaId || null);
    const body = {
      fecha: form.fecha,
      tipo: form.tipo,
      categoriaId: categoriaIdFinal,
      subcategoriaId: subcategoriaIdFinal,
      insumoId: form.insumoId || null,
      cantidad: form.cantidad ? Number(form.cantidad.replace(',', '.')) : null,
      unidad: form.unidad || null,
      descripcion: form.descripcion || null,
      metodoPago: form.metodoPago || null,
      monto,
    };
    if (form.id) actualizar.mutate({ id: form.id, body });
    else crear.mutate(body);
  }

  const activeFilters = (filtroTipo ? 1 : 0) + (desde ? 1 : 0) + (hasta ? 1 : 0) + (incluirAutomaticos ? 1 : 0);

  const resumen = useMemo(() => {
    const items = lista.data?.items ?? [];
    return items.reduce(
      (acc, m) => {
        const monto = Number(m.monto);
        if (m.tipo === 'compra') acc.compras += monto;
        else if (m.tipo === 'gasto') acc.gastos += monto;
        else acc.ingresos += monto;
        return acc;
      },
      { ingresos: 0, gastos: 0, compras: 0 },
    );
  }, [lista.data?.items]);

  const flujoCajaFiltrado = resumen.ingresos - resumen.gastos;

  const columns: Column<Movimiento>[] = [
    {
      key: 'movimiento',
      header: 'Movimiento',
      mobile: 'primary',
      render: (m) => (
        <div className="min-w-0">
          <div className="font-semibold text-slate-900 truncate">
            {m.descripcion || m.insumo?.nombre || m.categoria?.nombre || tipoLabel(m.tipo)}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-slate-500">
            <span>{fecha(m.fecha)}</span>
            <span className={`pill ${tipoPillClass(m.tipo)}`}>{tipoLabel(m.tipo)}</span>
            {m.metodoPago === 'automatico' && <span className="pill bg-slate-100 text-slate-600">Auto</span>}
          </div>
        </div>
      ),
    },
    {
      key: 'monto',
      header: 'Monto',
      mobile: 'secondary',
      align: 'right',
      render: (m) => (
        <span className={`font-semibold ${montoClass(m.tipo)}`}>
          {montoPrefix(m.tipo)}{cop(Number(m.monto))}
        </span>
      ),
    },
    {
      key: 'categoria',
      header: 'Categoría',
      render: (m) => (
        <span className="text-sm">
          {m.categoria?.nombre ?? '—'}
          {m.subcategoria && <span className="text-slate-400"> / {m.subcategoria.nombre}</span>}
        </span>
      ),
    },
    { key: 'insumo', header: 'Insumo', render: (m) => m.insumo?.nombre ?? '—' },
    {
      key: 'cant',
      header: 'Cantidad',
      align: 'right',
      render: (m) => (m.cantidad ? `${num(Number(m.cantidad), 2)} ${m.unidad ?? ''}` : '—'),
    },
    { key: 'pago', header: 'Método', render: (m) => m.metodoPago ?? '—' },
  ];

  const formJsx = (
    <form className="space-y-3" onSubmit={submit}>
      <div className="grid grid-cols-1 xs:grid-cols-2 gap-3">
        <div>
          <label className="label">Fecha</label>
          <input type="date" className="input" value={form.fecha} onChange={(e) => setForm((s) => ({ ...s, fecha: e.target.value }))} required />
        </div>
        <div>
          <label className="label">Tipo</label>
          <select className="input" value={form.tipo} onChange={(e) => setForm((s) => ({ ...s, tipo: e.target.value as TipoMovimiento, categoriaId: '', subcategoriaId: '' }))}>
            <option value="aporte">Aporte</option>
            <option value="ingreso">Ingreso</option>
            <option value="gasto">Gasto</option>
            <option value="compra">Compra (materia prima)</option>
          </select>
        </div>
      </div>

      {(form.tipo === 'gasto' || form.tipo === 'compra') && (
        <div className="grid grid-cols-1 xs:grid-cols-2 gap-3">
          <div>
            <label className="label">Insumo {form.tipo === 'compra' ? '(requerido)' : '(opcional)'}</label>
            <select className="input" value={form.insumoId} onChange={(e) => setForm((s) => ({ ...s, insumoId: e.target.value, unidad: insumosQ.data?.find((i) => i.id === e.target.value)?.unidad ?? s.unidad }))}>
              <option value="">— Sin insumo —</option>
              {insumosQ.data?.filter((i) => form.tipo !== 'compra' || i.conStock).map((i) => (
                <option key={i.id} value={i.id}>{i.nombre}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Cantidad</label>
            <input type="number" step="any" className="input" value={form.cantidad} onChange={(e) => setForm((s) => ({ ...s, cantidad: e.target.value }))} />
          </div>
        </div>
      )}

      {ocultarCategoria ? (
        <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm">
          {categoriaDelInsumo ? (
            <>Se registrará en categoría: <b>{categoriaDelInsumo.nombre}</b></>
          ) : (
            <span className="text-amber-700">
              El insumo <b>{insumoSeleccionado?.nombre}</b> no tiene categoría asignada.
              Podés fijarla en <b>Insumos → Editar</b>.
            </span>
          )}
        </div>
      ) : (
        <>
          <div>
            <label className="label">Categoría</label>
            <select className="input" value={form.categoriaId} onChange={(e) => setForm((s) => ({ ...s, categoriaId: e.target.value, subcategoriaId: '' }))}>
              <option value="">—</option>
              {categoriasDelTipo.map((c) => (
                <option key={c.id} value={c.id}>{c.nombre}</option>
              ))}
            </select>
          </div>

          {subcategorias.length > 0 && (
            <div>
              <label className="label">Subcategoría</label>
              <select className="input" value={form.subcategoriaId} onChange={(e) => setForm((s) => ({ ...s, subcategoriaId: e.target.value }))}>
                <option value="">—</option>
                {subcategorias.map((c) => (
                  <option key={c.id} value={c.id}>{c.nombre}</option>
                ))}
              </select>
            </div>
          )}
        </>
      )}

      <div className="grid grid-cols-1 xs:grid-cols-2 gap-3">
        <div>
          <label className="label">Monto (COP)</label>
          <input type="number" step="any" min="0" inputMode="decimal" className="input" value={form.monto} onChange={(e) => setForm((s) => ({ ...s, monto: e.target.value }))} required />
        </div>
        <div>
          <label className="label">Método de pago</label>
          <select className="input" value={form.metodoPago} onChange={(e) => setForm((s) => ({ ...s, metodoPago: e.target.value }))}>
            <option value="">—</option>
            <option value="Efectivo">Efectivo</option>
            <option value="Nequi">Nequi</option>
            <option value="Daviplata">Daviplata</option>
            <option value="Bancolombia">Bancolombia</option>
            <option value="Transferencia">Transferencia</option>
            <option value="Tarjeta">Tarjeta</option>
            <option value="Otro">Otro</option>
          </select>
        </div>
      </div>

      <div>
        <label className="label">Descripción</label>
        <input className="input" value={form.descripcion} onChange={(e) => setForm((s) => ({ ...s, descripcion: e.target.value }))} />
      </div>

      {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">{error}</div>}

      <div className="flex flex-col xs:flex-row gap-2">
        {form.id && (
          <button type="button" className="btn-secondary flex-1" onClick={limpiarFormulario}>
            Cancelar
          </button>
        )}
        <button type="submit" className="btn-primary flex-1" disabled={crear.isPending || actualizar.isPending}>
          {crear.isPending || actualizar.isPending
            ? 'Guardando...'
            : form.id
              ? 'Guardar cambios'
              : 'Registrar movimiento'}
        </button>
      </div>
    </form>
  );

  return (
    <div className="space-y-4 sm:space-y-6">
      <PageHeader
        title="Movimientos"
        subtitle="Libro diario del efectivo: entradas, compras, gastos y aportes."
      />

      <section className="grid grid-cols-2 lg:grid-cols-5 gap-2 sm:gap-3">
        <div className="card px-3 py-3 sm:px-4">
          <div className="text-[10px] sm:text-xs uppercase tracking-wide text-slate-500">Saldo actual</div>
          <div className="mt-1 text-lg sm:text-xl font-semibold text-slate-950">{cop(saldoQ.data?.saldo)}</div>
        </div>
        <div className="card px-3 py-3 sm:px-4">
          <div className="text-[10px] sm:text-xs uppercase tracking-wide text-slate-500">Entradas filtradas</div>
          <div className="mt-1 text-lg sm:text-xl font-semibold text-emerald-700">{cop(resumen.ingresos)}</div>
        </div>
        <div className="card px-3 py-3 sm:px-4">
          <div className="text-[10px] sm:text-xs uppercase tracking-wide text-slate-500">Gastos filtrados</div>
          <div className="mt-1 text-lg sm:text-xl font-semibold text-red-600">{cop(resumen.gastos)}</div>
        </div>
        <div className="card px-3 py-3 sm:px-4">
          <div className="text-[10px] sm:text-xs uppercase tracking-wide text-slate-500">Compras inventario</div>
          <div className="mt-1 text-lg sm:text-xl font-semibold text-amber-700">{cop(resumen.compras)}</div>
        </div>
        <div className="card px-3 py-3 sm:px-4">
          <div className="text-[10px] sm:text-xs uppercase tracking-wide text-slate-500">Flujo caja filtrado</div>
          <div className={`mt-1 text-lg sm:text-xl font-semibold ${flujoCajaFiltrado < 0 ? 'text-red-600' : 'text-slate-950'}`}>
            {cop(flujoCajaFiltrado)}
          </div>
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
        {/* Form en desktop como columna; en móvil como sheet */}
        {tiene('movimientos:write') && !isMobile && (
          <div className="card p-5 space-y-4 lg:col-span-1 lg:sticky lg:top-6 lg:self-start">
            <div>
              <h2 className="font-semibold text-slate-950">{form.id ? 'Editar movimiento' : 'Nuevo movimiento'}</h2>
              <p className="mt-1 text-xs text-slate-500">
                {form.id ? 'Actualiza el registro seleccionado.' : 'Registra una entrada o salida sin salir del historial.'}
              </p>
            </div>
            {formJsx}
          </div>
        )}

        <div className={`card ${tiene('movimientos:write') && !isMobile ? 'lg:col-span-2' : 'lg:col-span-3'}`}>
          <div className="p-3 sm:p-4 border-b border-slate-200 space-y-3 bg-slate-50/60 rounded-t-lg">
            <FilterBar activeCount={activeFilters}>
              <div>
                <label className="label">Tipo</label>
                <select className="input" value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value as '' | TipoMovimiento)}>
                  <option value="">Todos</option>
                  <option value="aporte">Aportes</option>
                  <option value="ingreso">Ingresos</option>
                  <option value="gasto">Gastos</option>
                  <option value="compra">Compras</option>
                </select>
              </div>
              <div>
                <label className="label">Desde</label>
                <input type="date" className="input" value={desde} onChange={(e) => setDesde(e.target.value)} />
              </div>
              <div>
                <label className="label">Hasta</label>
                <input type="date" className="input" value={hasta} onChange={(e) => setHasta(e.target.value)} />
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700 sm:self-end min-h-10">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300"
                  checked={incluirAutomaticos}
                  onChange={(e) => setIncluirAutomaticos(e.target.checked)}
                />
                Ver automáticos
              </label>
            </FilterBar>
            <div className="hidden md:flex items-center justify-between gap-3 text-sm text-slate-600">
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                <span><b>{lista.data?.total ?? 0}</b> registros</span>
                <span>Periodo: <b>{desde || 'inicio'}</b> a <b>{hasta || 'hoy'}</b></span>
              </div>
              <button
                type="button"
                className="btn-secondary"
                onClick={exportarXlsx}
                disabled={!lista.data?.items?.length}
                title="Descargar como .xlsx"
              >
                ⬇ .xlsx
              </button>
            </div>
            <div className="md:hidden space-y-2">
              <div className="flex items-center justify-between text-xs text-slate-500">
                <span><b>{lista.data?.total ?? 0}</b> registros</span>
                <span>{activeFilters > 0 ? `${activeFilters} filtros activos` : 'Sin filtros'}</span>
              </div>
              {tiene('movimientos:write') && (
                <button
                  type="button"
                  className="btn-primary w-full"
                  onClick={() => {
                    limpiarFormulario();
                    setSheetOpen(true);
                  }}
                >
                  Nuevo movimiento
                </button>
              )}
              <button
                type="button"
                className="btn-secondary w-full"
                onClick={exportarXlsx}
                disabled={!lista.data?.items?.length}
              >
                ⬇ Exportar .xlsx
              </button>
            </div>
          </div>
          <ResponsiveTable<Movimiento>
            columns={columns}
            rows={lista.data?.items}
            rowKey={(m) => m.id}
            loading={lista.isLoading}
            empty="Sin movimientos."
            actions={(m) =>
              tiene('movimientos:write') && m.metodoPago !== 'automatico' ? (
                <div className="flex flex-wrap justify-end gap-2">
                  <button
                    type="button"
                    className="btn-secondary text-xs"
                    onClick={() => editarMovimiento(m)}
                  >
                    Editar
                  </button>
                  <button
                    type="button"
                    className="btn-danger text-xs"
                    onClick={() => {
                      if (confirm('¿Eliminar este movimiento?')) eliminar.mutate(m.id);
                    }}
                  >
                    Eliminar
                  </button>
                </div>
              ) : null
            }
          />
        </div>
      </div>

      {tiene('movimientos:write') && (
        <>
          <MobileFormSheet
            open={sheetOpen}
            onClose={() => setSheetOpen(false)}
            title={form.id ? 'Editar movimiento' : 'Nuevo movimiento'}
            inlineOnDesktop={false}
          >
            {formJsx}
          </MobileFormSheet>
        </>
      )}
    </div>
  );
}
